import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  Print as PrintIcon, Description as WordIcon, TableChart as ExcelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Collection Sheet — a printable empty-cell grid teachers fill in by hand.
// Generated from school info + a class roster + selected income types + days.

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// ── CSS — used by both the in-dialog preview AND the print/export HTML ──
const SHEET_CSS = `
  @page { size: A4 landscape; margin: 10mm; }
  .cs-sheet {
    position: relative; overflow: hidden;
    background: #fff; color: #000;
    font-family: 'Times New Roman', Georgia, serif;
    padding: 24px 32px; max-width: 1180px; margin: 0 auto;
    box-sizing: border-box;
  }
  /* Watermark sits behind everything; absolute against the relative .cs-sheet
     so it lands centrally regardless of the surrounding modal/page chrome. */
  .cs-watermark {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%) rotate(-22deg);
    width: 65%; max-width: 600px;
    opacity: 0.12; pointer-events: none; user-select: none;
    z-index: 0;
  }
  .cs-content { position: relative; z-index: 1; }
  .cs-header { display: table; width: 100%; }
  .cs-header > div { display: table-cell; vertical-align: middle; }
  .cs-logo { width: 70px; height: 70px; object-fit: contain; display: block; }
  .cs-school { font-weight: 800; font-size: 22px; text-decoration: underline;
    text-transform: uppercase; text-align: center; letter-spacing: 0.5px; }
  .cs-info { width: 100%; border-collapse: collapse; font-size: 12px; margin: 4px 0 6px 0; }
  .cs-info td { padding: 3px 6px; vertical-align: top; }
  .cs-info .lbl { font-weight: 700; white-space: nowrap; }
  .cs-divider { border: none; border-top: 1px solid #000; margin: 6px 0 10px 0; }
  .cs-title { text-align: center; font-weight: 800; font-size: 16px;
    text-decoration: underline; margin: 4px 0 8px 0; }
  .cs-meta { display: table; width: 100%; font-size: 12px; margin-bottom: 6px; }
  .cs-meta > div { display: table-cell; padding: 2px 4px; }
  .cs-meta b { font-weight: 700; }
  .cs-table { width: 100%; border-collapse: collapse; font-size: 11px; table-layout: fixed;
              background: rgba(255,255,255,0.85); }
  .cs-table th, .cs-table td { border: 1px solid #000; padding: 4px 4px; }
  .cs-table th { background: #f0f0f0; text-align: center; font-weight: 700; }
  .cs-table thead th.group { font-size: 12px; }
  .cs-table thead th.day   { font-weight: 700; }
  .cs-table td.idx  { text-align: center; width: 28px; font-weight: 600; }
  .cs-table td.name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cs-table td.cell { height: 22px; }
  .cs-table tr.totalrow td { background: #fafafa; font-weight: 800; }
  .cs-signatures { display: table; width: 100%; margin-top: 28px; font-size: 12px; }
  .cs-signatures > div { display: table-cell; padding: 0 16px; }
  .cs-signatures .line { border-top: 1px dotted #000; padding-top: 4px; font-weight: 700; }
  @media print {
    body { background: #fff; margin: 0; }
    .cs-sheet { padding: 8mm; max-width: 100%; }
    .cs-watermark { opacity: 0.15; }
  }
`;

function buildSheetHTML({ school, students, selectedTypes, days, cla, term, year, weekStart }) {
  const groups = selectedTypes;          // [{ id, type }]
  // Each group has N day columns + 1 total column => widths balance.
  const headerGroupCells = groups.map(g =>
    `<th class="group" colspan="${days.length + 1}">${esc(g.type)}</th>`
  ).join('');
  const dayHeaderCells = groups.map(() => {
    const d = days.map(d => `<th class="day">${esc(d)}</th>`).join('');
    const tot = `<th class="day">Total</th>`;
    return d + tot;
  }).join('');
  const cellsPerStudent = groups.map(() =>
    `${days.map(() => '<td class="cell"></td>').join('')}<td class="cell"></td>`
  ).join('');

  const rows = (students.length === 0
    ? `<tr><td colspan="${3 + groups.length * (days.length + 1) + 1}" style="text-align:center;color:#666;padding:14px;">No students for this class.</td></tr>`
    : students.map((s, i) => `
        <tr>
          <td class="idx">${i + 1}</td>
          <td class="name">${esc(s.name)}</td>
          <td class="name">${esc(s.index_no)}</td>
          ${cellsPerStudent}
          <td class="cell"></td>
        </tr>`).join('')
  );

  // Totals row at the bottom
  const totalCells = groups.map(() =>
    `${days.map(() => '<td class="cell"></td>').join('')}<td class="cell"></td>`
  ).join('');

  const watermark = school?.logo
    ? `<img class="cs-watermark" src="${esc(school.logo)}" alt="" />`
    : '';
  const logo = school?.logo
    ? `<img class="cs-logo" src="${esc(school.logo)}" alt="logo" />`
    : '';

  return `
    ${watermark}
    <div class="cs-content">
      <div class="cs-header">
        <div style="width:70px;">${logo}</div>
        <div><div class="cs-school">${esc(school?.school_name || 'School Name')}</div></div>
        <div style="width:70px;"></div>
      </div>

      <table class="cs-info">
        <tr>
          <td class="lbl">Address:</td><td>${esc(school?.address || '—')}</td>
          <td class="lbl" style="text-align:right;">Tel#:</td>
          <td style="text-align:right;">${esc(school?.phone || '—')}</td>
        </tr>
        <tr>
          <td class="lbl">Location:</td><td>${esc(school?.region || '—')}</td>
          <td class="lbl" style="text-align:right;">Email:</td>
          <td style="text-align:right;">${esc(school?.email || '—')}</td>
        </tr>
        <tr>
          <td class="lbl">Mission:</td><td>${esc(school?.motto || '—')}</td>
          <td class="lbl" style="text-align:right;">Website:</td>
          <td style="text-align:right;">-</td>
        </tr>
      </table>
      <hr class="cs-divider" />

      <div class="cs-title">Daily Collection Sheet</div>

      <div class="cs-meta">
        <div><b>Class:</b> ${esc(cla || '—')}</div>
        <div><b>Term:</b> ${esc(term || '—')}</div>
        <div><b>Academic Year:</b> ${esc(year || '—')}</div>
        <div style="text-align:right;"><b>Week of:</b> ${esc(fmtDate(weekStart))}</div>
      </div>

      <table class="cs-table">
        <thead>
          <tr>
            <th rowspan="2" style="width:28px;">#</th>
            <th rowspan="2" style="width:170px;">Student Name</th>
            <th rowspan="2" style="width:80px;">Index</th>
            ${headerGroupCells}
            <th rowspan="2" style="width:70px;">Grand Total</th>
          </tr>
          <tr>
            ${dayHeaderCells}
          </tr>
        </thead>
        <tbody>
          ${rows}
          <tr class="totalrow">
            <td colspan="3" style="text-align:right;">TOTAL</td>
            ${totalCells}
            <td class="cell"></td>
          </tr>
        </tbody>
      </table>

      <div class="cs-signatures">
        <div><div class="line">Class Teacher</div></div>
        <div><div class="line">Collector</div></div>
        <div><div class="line">Accountant</div></div>
      </div>
    </div>
  `;
}

function fullDocument(innerHTML, title) {
  return `<!DOCTYPE html><html><head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>${SHEET_CSS}</style>
  </head><body><div class="cs-sheet">${innerHTML}</div></body></html>`;
}

function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function CollectionSheetDialog({
  open, onClose, loading, error,
  school, students = [], selectedTypes = [], days = [],
  cla, term, year, weekStart,
}) {
  const html = buildSheetHTML({ school, students, selectedTypes, days, cla, term, year, weekStart });
  const title = `Collection-Sheet-${cla || ''}-${weekStart || ''}`.replace(/[^A-Za-z0-9_-]/g, '_');

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) return;
    w.document.write(fullDocument(html, title));
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 350);
  };
  const handleExportWord = () => {
    downloadBlob(fullDocument(html, title), 'application/msword', `${title}.doc`);
  };
  const handleExportExcel = () => {
    // Excel reads HTML tables; serve as .xls for Excel to open natively.
    downloadBlob(fullDocument(html, title), 'application/vnd.ms-excel', `${title}.xls`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: '#eef0f4' }}>
        {loading ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} color="text.secondary">Building sheet…</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>
        ) : (
          <Box sx={{ py: 3, maxWidth: 1240, mx: 'auto', px: { xs: 1, sm: 2 } }}>
            <Box sx={{ bgcolor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              borderRadius: 1, overflow: 'auto' }}>
              <style>{SHEET_CSS}</style>
              <div className="cs-sheet" dangerouslySetInnerHTML={{ __html: html }} />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: '#fff', borderTop: '1px solid', borderColor: 'divider' }}>
        <Tooltip title="Close"><IconButton onClick={onClose}><CloseIcon /></IconButton></Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleExportExcel} startIcon={<ExcelIcon />}
          variant="outlined" sx={{ textTransform: 'none', fontWeight: 700 }}>Excel</Button>
        <Button onClick={handleExportWord} startIcon={<WordIcon />}
          variant="outlined" sx={{ textTransform: 'none', fontWeight: 700 }}>Word</Button>
        <Button onClick={handlePrint} startIcon={<PrintIcon />}
          variant="contained" sx={{ textTransform: 'none', fontWeight: 800, px: 3 }}>Print</Button>
      </DialogActions>
    </Dialog>
  );
}
